import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

export type UrlListSortDirection = 'asc' | 'desc'

/** Имена query-параметров в URL; ключ можно опустить — соответствующий фильтр выключен. */
export type UrlListFiltersQueryParams = {
    /** Текстовый поиск с debounce. */
    search?: string
    /** Одно строковое значение (маркетплейс, категория и т.п.). */
    slug?: string
    orderBy?: string
    direction?: string
    /** Числовой фильтр (например оценка), строка в URL. */
    score?: string
    /** Источник (enum), строка в URL. */
    source?: string
    /** Бренд (произвольная строка), строка в URL. */
    brand?: string
    /** Продавец (произвольная строка), строка в URL. */
    seller?: string
}

export const defaultUrlListQueryParams = {
    search: 'q',
    orderBy: 'order_by',
    direction: 'direction',
} as const satisfies UrlListFiltersQueryParams

export type UseUrlListFiltersOptions = {
    /** Параметр URL, сбрасываемый при смене фильтров (например позиция скролла). */
    scrollParam?: string
    /** Имена query-параметров; можно передать частично — к `defaultUrlListQueryParams` или к явному базису. */
    queryParams?: Partial<UrlListFiltersQueryParams>
    debounceMs?: number
    defaultOrderBy?: string
    omitOrderByWhenDefault?: boolean
    orderByAllowlist?: readonly string[]
    defaultDirection?: UrlListSortDirection
    omitDirectionWhenDefault?: boolean
    slugAllowlist?: readonly string[]
    /** Allowlist для `queryParams.source` (например `ProductSource`). */
    sourceFilterAllowlist?: readonly string[]
}

export type UrlListFiltersResult = {
    /** Текст поиска (trim), для API часто как `q`. */
    q: string
    /** Строка для подсветки совпадений в списке (совпадает с `q` после применения из URL). */
    searchHighlight: string
    /** Распознанное значение «slug»-фильтра (allowlist). */
    slug: string | undefined
    /** Число из URL при заданном `queryParams.score`. */
    score: number | undefined
    /** Значение `queryParams.source` после allowlist. */
    filterSource: string | undefined
    /** Значение `queryParams.brand` (trim, без allowlist). */
    filterBrand: string | undefined
    /** Значение `queryParams.seller` (trim, без allowlist). */
    filterSeller: string | undefined
    orderBy: string
    direction: UrlListSortDirection
    searchDraft: string
    setSearchDraft: (value: string) => void
    slugSelectValue: string
    scoreSelectValue: string
    sourceSelectValue: string
    brandSelectValue: string
    sellerSelectValue: string
    orderBySelectValue: string
    directionSelectValue: UrlListSortDirection
    handleSlugChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleScoreChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleSourceChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleBrandChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleSellerChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleOrderByChange: (e: ChangeEvent<HTMLSelectElement>) => void
    handleDirectionChange: (e: ChangeEvent<HTMLSelectElement>) => void
    filterKey: string
}

const parseDirectionFromSearch = (raw: string | null, defaultDirection: UrlListSortDirection): UrlListSortDirection => {
    if (raw === 'asc' || raw === 'desc') return raw
    return defaultDirection
}

const parseSlugValue = (raw: string | null, allowlist: readonly string[] | undefined): string | undefined => {
    const trimmed = raw?.trim()
    if (!trimmed) return undefined
    if (allowlist && !allowlist.includes(trimmed)) return undefined
    return trimmed
}

const parseScoreParam = (raw: string | null): number | undefined => {
    const trimmed = raw?.trim()
    if (!trimmed || !/^\d+$/.test(trimmed)) return undefined
    return Number(trimmed)
}

const parseOrderByValue = (
    raw: string | null,
    allowlist: readonly string[] | undefined,
    defaultOrderBy: string
): string => {
    const trimmed = raw?.trim()
    if (!trimmed) return defaultOrderBy
    if (allowlist && !allowlist.includes(trimmed)) return defaultOrderBy
    return trimmed
}

const mergeQueryParams = (partial?: Partial<UrlListFiltersQueryParams>): UrlListFiltersQueryParams => ({
    ...defaultUrlListQueryParams,
    ...partial,
})

export const useUrlListFilters = (options: UseUrlListFiltersOptions = {}): UrlListFiltersResult => {
    const {
        scrollParam = 'scroll',
        queryParams: queryParamsPartial,
        debounceMs = 300,
        defaultOrderBy = 'updated_at',
        omitOrderByWhenDefault = true,
        orderByAllowlist,
        defaultDirection = 'desc',
        omitDirectionWhenDefault = true,
        slugAllowlist,
        sourceFilterAllowlist,
    } = options

    const qp = mergeQueryParams(queryParamsPartial)
    const searchParam = qp.search
    const slugParam = qp.slug
    const orderByParam = qp.orderBy
    const directionParam = qp.direction
    const scoreParam = qp.score
    const sourceParam = qp.source
    const brandParam = qp.brand
    const sellerParam = qp.seller

    const [searchParams, setSearchParams] = useSearchParams()

    const qFromUrl = searchParam ? (searchParams.get(searchParam) ?? '') : ''
    const slugFromUrl = slugParam ? (searchParams.get(slugParam) ?? '') : ''
    const scoreFromUrl = scoreParam ? (searchParams.get(scoreParam) ?? '') : ''
    const sourceFromUrl = sourceParam ? (searchParams.get(sourceParam) ?? '') : ''
    const brandFromUrl = brandParam ? (searchParams.get(brandParam) ?? '') : ''
    const sellerFromUrl = sellerParam ? (searchParams.get(sellerParam) ?? '') : ''
    const orderByFromUrl = orderByParam ? (searchParams.get(orderByParam) ?? '') : ''
    const directionFromUrl = directionParam
        ? parseDirectionFromSearch(searchParams.get(directionParam), defaultDirection)
        : defaultDirection

    const q = qFromUrl.trim()
    const slug = slugParam ? parseSlugValue(slugFromUrl || null, slugAllowlist) : undefined
    const score = scoreParam ? parseScoreParam(scoreFromUrl || null) : undefined
    const filterSource = sourceParam ? parseSlugValue(sourceFromUrl || null, sourceFilterAllowlist) : undefined
    const filterBrand = brandParam ? parseSlugValue(brandFromUrl || null, undefined) : undefined
    const filterSeller = sellerParam ? parseSlugValue(sellerFromUrl || null, undefined) : undefined
    const orderBy = orderByParam
        ? parseOrderByValue(orderByFromUrl || null, orderByAllowlist, defaultOrderBy)
        : defaultOrderBy
    const direction = directionFromUrl

    const [searchDraft, setSearchDraft] = useState(qFromUrl)

    useEffect(() => {
        if (!searchParam) return
        setSearchDraft(qFromUrl)
    }, [qFromUrl, searchParam])

    useEffect(() => {
        if (!searchParam) return
        const id = window.setTimeout(() => {
            const trimmed = searchDraft.trim()
            setSearchParams(
                prev => {
                    const current = (prev.get(searchParam) ?? '').trim()
                    if (trimmed === current) return prev
                    const next = new URLSearchParams(prev)
                    if (trimmed) next.set(searchParam, trimmed)
                    else next.delete(searchParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        }, debounceMs)
        return () => window.clearTimeout(id)
    }, [searchDraft, setSearchParams, searchParam, scrollParam, debounceMs])

    const filterSignatureRef = useRef<string | null>(null)
    useEffect(() => {
        const parts: string[] = []
        if (searchParam) parts.push(q)
        if (slugParam) parts.push(slug ?? '')
        if (scoreParam) parts.push(score !== undefined ? String(score) : '')
        if (sourceParam) parts.push(filterSource ?? '')
        if (brandParam) parts.push(filterBrand ?? '')
        if (sellerParam) parts.push(filterSeller ?? '')
        if (orderByParam) parts.push(orderBy)
        if (directionParam) parts.push(direction)
        const sig = parts.join('|')

        if (filterSignatureRef.current === null) {
            filterSignatureRef.current = sig
            return
        }
        if (filterSignatureRef.current === sig) return
        filterSignatureRef.current = sig
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                next.delete(scrollParam)
                return next
            },
            { replace: true }
        )
    }, [
        q,
        slug,
        score,
        filterSource,
        filterBrand,
        filterSeller,
        orderBy,
        direction,
        scrollParam,
        setSearchParams,
        searchParam,
        slugParam,
        scoreParam,
        sourceParam,
        brandParam,
        sellerParam,
        orderByParam,
        directionParam,
    ])

    const handleSlugChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!slugParam) return
            const v = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (v) next.set(slugParam, v)
                    else next.delete(slugParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [slugParam, scrollParam, setSearchParams]
    )

    const handleScoreChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!scoreParam) return
            const v = e.target.value.trim()
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (v) next.set(scoreParam, v)
                    else next.delete(scoreParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [scoreParam, scrollParam, setSearchParams]
    )

    const handleSourceChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!sourceParam) return
            const v = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (v) next.set(sourceParam, v)
                    else next.delete(sourceParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [sourceParam, scrollParam, setSearchParams]
    )

    const handleBrandChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!brandParam) return
            const v = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (v) next.set(brandParam, v)
                    else next.delete(brandParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [brandParam, scrollParam, setSearchParams]
    )

    const handleSellerChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!sellerParam) return
            const v = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (v) next.set(sellerParam, v)
                    else next.delete(sellerParam)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [sellerParam, scrollParam, setSearchParams]
    )

    const handleOrderByChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!orderByParam) return
            const v = e.target.value
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (omitOrderByWhenDefault && v === defaultOrderBy) next.delete(orderByParam)
                    else next.set(orderByParam, v)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [defaultOrderBy, omitOrderByWhenDefault, orderByParam, scrollParam, setSearchParams]
    )

    const handleDirectionChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            if (!directionParam) return
            const v = e.target.value as UrlListSortDirection
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (omitDirectionWhenDefault && v === defaultDirection) next.delete(directionParam)
                    else next.set(directionParam, v)
                    next.delete(scrollParam)
                    return next
                },
                { replace: true }
            )
        },
        [defaultDirection, directionParam, omitDirectionWhenDefault, scrollParam, setSearchParams]
    )

    const filterKey = useMemo(() => {
        const parts: string[] = []
        if (searchParam) parts.push(q)
        if (slugParam) parts.push(slug ?? '')
        if (scoreParam) parts.push(score !== undefined ? String(score) : '')
        if (sourceParam) parts.push(filterSource ?? '')
        if (brandParam) parts.push(filterBrand ?? '')
        if (sellerParam) parts.push(filterSeller ?? '')
        if (orderByParam) parts.push(orderBy)
        if (directionParam) parts.push(direction)
        return parts.join('|')
    }, [
        q,
        slug,
        score,
        filterSource,
        filterBrand,
        filterSeller,
        orderBy,
        direction,
        searchParam,
        slugParam,
        scoreParam,
        sourceParam,
        brandParam,
        sellerParam,
        orderByParam,
        directionParam,
    ])

    return {
        q,
        searchHighlight: q,
        slug,
        score,
        filterSource,
        filterBrand,
        filterSeller,
        orderBy,
        direction,
        searchDraft,
        setSearchDraft,
        slugSelectValue: slugFromUrl,
        scoreSelectValue: scoreFromUrl,
        sourceSelectValue: sourceFromUrl,
        brandSelectValue: brandFromUrl,
        sellerSelectValue: sellerFromUrl,
        orderBySelectValue: orderBy,
        directionSelectValue: directionFromUrl,
        handleSlugChange,
        handleScoreChange,
        handleSourceChange,
        handleBrandChange,
        handleSellerChange,
        handleOrderByChange,
        handleDirectionChange,
        filterKey,
    }
}
